import {FC, useEffect, useState, ReactElement, useId} from 'react';
import {Box, Grid, InputAdornment, TextField, Typography} from '@mui/material';
import {t} from 'i18next';

import AurIcon, {IconNames, IconColors} from '../../../common/aur-icon/AurIcon';

import {
  CustomIndexInfoFormInputKey,
  CUSTOM_INDEX_NAME_MAX_LENGTH,
} from '../../../pages/user-management/models';
import AurButton from '../../../common/aur-button/AurButton';
import {
  setConfirmModal,
  setIndexCustom,
} from '../../../../redux/slices/indexingSlice';
import {useDispatch, useSelector} from 'react-redux';
import AurSearchBox from '../../../common/aur-search-box/AurSearchBox';
import SavedSearchPanel from '../../../eca/SavedSearchPanel';
import {MIN_SEARCH_CHAR} from '../../../../constants/constants';
import {RootState} from '../../../../redux/store';
import {isEmpty} from '../../../../utils/utils';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../eca-doc-list/eca-save-search/EcaSaveSearch';

interface CustomIndexFormFieldsProps {
  methods: any;
  buttonVisible?: boolean;
  setActiveStep?: any;
}

interface SavedSearchType {
  id: string;
  label: string;
}

const CustomIndexFormFieldsStep1: FC<CustomIndexFormFieldsProps> = (props) => {
  const {setActiveStep, methods} = props;
  const {trackGAEvent} = useAnalytics();

  const {
    getValues,
    setValue,
    formState: {isValid, errors},
    control,
    watch,
  } = methods;

  const savedSearchLastRun = useSelector(
    (state: RootState) => state.ecaAdhocSearch.savedSearchLastRun
  );

  const indexCustom = useSelector(
    (state: RootState) => state.indexes.indexCustom
  );

  const dispatch = useDispatch();

  const OpenPopupHandler = () => {
    dispatch(setConfirmModal(true));
  };
  const hintID = useId();

  const stringPath = 'eca.savedSearchSelectionDialog.';
  const [searchInput, setSearchInput] = useState<string>('');
  const selectedIndex = 'Standard Index';
  const [selectedSearchDetailUI, setSelectedSearchDetailUI] =
    useState<ReactElement | null>(null);
  const [selectedSavedSearch, setSelectedSavedSearch] =
    useState<SavedSearchType | null>(null);
  const [customNameError, setCustomNameError] = useState<string>('');
  const [selectedSearchNode, setSelectedSearchNode] =
    useState<TreeViewBaseItem<ExtendedTreeItemProps> | null>(null);

  const indexingList = useSelector(
    (state: RootState) => state.indexes.indexingList
  );
  const customIndexValue = watch(CustomIndexInfoFormInputKey.customIndex);

  useEffect(() => {
    if (!indexCustom) return;

    if (indexCustom.indexName) {
      setValue(CustomIndexInfoFormInputKey.customIndex, indexCustom.indexName, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
    if (indexCustom.savedSearch) {
      setSelectedSearchNode(indexCustom.savedSearch);
      setSelectedSavedSearch({
        id: indexCustom.savedSearch.id,
        label: indexCustom.savedSearch.label,
      });
    }
  }, [indexCustom, setValue]);

  useEffect(() => {
    if (!customIndexValue) {
      setCustomNameError('');
      return;
    }
    const nameExists = indexingList?.some(
      (index) =>
        index?.name?.trim().toLowerCase() ===
          customIndexValue.trim().toLowerCase() && index?.type === 'Custom'
    );

    if (nameExists) {
      setCustomNameError(t('indexes.customIndex.step1.duplicateCustomIndex'));
    } else {
      setCustomNameError('');
    }
  }, [customIndexValue]);

  return (
    <Box>
      <Grid container className="user-form">
        <Grid item sm={12} className="form-content userform-container">
          <div className="create-custom-index-container padding-left-144">
            <Typography className="typo-text headline3_size selectedNumberText custom-index-title ">
              {t('indexes.customIndex.step1.createCustomIndexTitle')}
            </Typography>
            <span id={hintID} className="sr-only">
              {t('accessibility.indexManagement.detail.createCustomIndexHint', {
                count: CUSTOM_INDEX_NAME_MAX_LENGTH,
              })}
            </span>
            <TextField
              {...control.register('customIndex', {required: true})}
              name={CustomIndexInfoFormInputKey.customIndex}
              className="team-member-info-field"
              inputProps={{
                'data-testid': 'custom-index',
                maxLength: CUSTOM_INDEX_NAME_MAX_LENGTH,
                'aria-describedby': hintID,
                'aria-label': t(
                  'indexes.customIndex.step1.createCustomIndexTitle'
                ),
              }}
              InputProps={{
                disableUnderline: true,
                endAdornment:
                  errors?.customIndex?.message || customNameError !== '' ? (
                    <InputAdornment position="end">
                      <AurIcon
                        icon={IconNames.Error}
                        color={IconColors.Critical}
                      />
                    </InputAdornment>
                  ) : null,

                className:
                  (errors?.customIndex?.message || customNameError !== '') &&
                  'inputError',
              }}
              variant="filled"
              label={t('indexes.customIndex.step1.indexName')}
              helperText={
                errors.customIndex?.message
                  ? errors.customIndex?.message
                  : customNameError !== '' && customNameError
              }
              InputLabelProps={{
                className:
                  (errors?.customIndex?.message || customNameError !== '') &&
                  'label-error',
              }}
              FormHelperTextProps={{
                className:
                  (errors?.customIndex?.message || customNameError !== '') &&
                  'label-error',
              }}
            />
            {errors ? (
              <Typography className="typo-text body3_size search-desc-text">
                {t('indexes.customIndex.step1.customIndexHelperText')}
              </Typography>
            ) : null}
          </div>

          <div className="saved-search-content search-input-layout padding-left-144">
            <Typography className="typo-text body3_size selectedNumberText">
              {t('indexes.customIndex.step1.savedSearchTitle')}
            </Typography>

            <Typography className="typo-text body3_size search-desc-text">
              {t('indexes.customIndex.step1.savedSearchDescription')}
            </Typography>

            <AurSearchBox
              label={t(stringPath + 'searchSavedSearches')}
              parentClassName="search-field-input-div"
              classNameProps="text-field"
              minCharForSearch={MIN_SEARCH_CHAR}
              onSearch={setSearchInput}
              dataTestId="search-input"
            />
          </div>
          <div className="saved-search-side-panel-drawer index-saved-search">
            <div className="content-container">
              <div className="panel-container">
                <div className="left-search-list-tree">
                  <SavedSearchPanel
                    selectedIndex={selectedIndex ?? ''}
                    enableFlayout={false}
                    renderSelectedSearchDetail={setSelectedSearchDetailUI}
                    onPickSavedSearch={setSelectedSavedSearch}
                    onPickTreeItem={setSelectedSearchNode}
                    selectedSearch={indexCustom?.savedSearch ?? null}
                    expandedIds={
                      indexCustom?.savedSearch
                        ? indexCustom?.savedSearch?.directory_structure_ids?.split(
                            '/'
                          )
                        : undefined
                    }
                    openForPickSavedSearch
                    enableSearch={false}
                    searchInput={searchInput}
                    handleSearchId={true}
                  />
                </div>
                <div
                  className={`${selectedSearchDetailUI ? 'right-search-detail' : 'border-left-hide'} create-custom-search`}
                >
                  {!isEmpty(selectedSavedSearch) && selectedSearchDetailUI}
                </div>
              </div>
            </div>
          </div>
        </Grid>
      </Grid>
      <Box className="index-action-container" style={{margin: '0'}}>
        <div className="custom-index-action-button">
          <AurButton
            type="exit"
            label={t('buttons.cancel')}
            title={t('buttons.cancel')}
            size="Medium"
            onClick={() => OpenPopupHandler()}
            dataTestId={'closeBtn-test'}
            classNames="index-cancel-btn"
            ariaDescription={t(
              'accessibility.indexManagement.create.cancelHint'
            )}
          />
          <AurButton
            type="continue"
            label={t('buttons.next')}
            title={t('buttons.next')}
            ariaDescription={t(
              'accessibility.indexManagement.create.continueHint'
            )}
            size="Medium"
            onClick={() => {
              dispatch(
                setIndexCustom({
                  indexName: getValues(CustomIndexInfoFormInputKey.customIndex),
                  savedSearchId: selectedSavedSearch?.id
                    ? selectedSavedSearch?.id
                    : '',
                  savedSearch: selectedSearchNode,
                })
              );
              trackGAEvent({
                eventName: GA_EVENT_NAMES.CLICK,
                click: {
                  name: t('gaTracking.indexManagement.next'),
                  type: GA_CLICK_TYPES.BUTTON,
                  region: GA_REGIONS.TAB,
                  action: t('gaTracking.indexManagement.generalInfoEntered'),
                },
                subPage: {
                  subpage_name: t('gaTracking.indexManagement.generalInfo'),
                  subpage_type: GA_SUBPAGE_TYPE.TAB,
                },
              });
              setActiveStep((prev: any) => prev + 1);
            }}
            dataTestId={'closeBtn-test'}
            disabled={
              !isValid ||
              selectedSavedSearch == null ||
              savedSearchLastRun ||
              customNameError !== ''
            }
          />
        </div>
      </Box>
    </Box>
  );
};

export default CustomIndexFormFieldsStep1;
